import { useState, useRef, useEffect } from 'react';

const TYPE_MATRIX = {
  LOGICAL_ARCHITECT: {
    sprinter:     { name: 'スプリント思考型',        sub: 'Sprint Thinker',          color1: '#6366f1', color2: '#a78bfa', emoji: '🧠', tag: '短期集中で本質を掴む分析家' },
    climber:      { name: 'ディープダイブ型',        sub: 'Deep Diver',              color1: '#3b82f6', color2: '#6366f1', emoji: '🔬', tag: '一つのテーマを徹底的に掘り下げる' },
    phoenix:      { name: 'リフレクション型',        sub: 'Reflective Thinker',      color1: '#8b5cf6', color2: '#a78bfa', emoji: '💡', tag: '失敗から最も多くを学ぶ思考家' },
    breakthrough: { name: 'インサイト型',            sub: 'Insight Seeker',          color1: '#4f46e5', color2: '#7c3aed', emoji: '🎯', tag: 'じっくり熟考してから大きく動く' },
    marathoner:   { name: 'システム思考型',          sub: 'Systems Thinker',         color1: '#2563eb', color2: '#4f46e5', emoji: '🗂️', tag: '長期視点で構造を設計し続ける' },
  },
  EMPATHETIC_CONNECTOR: {
    sprinter:     { name: 'ファシリテーター型',      sub: 'Natural Facilitator',     color1: '#10b981', color2: '#06b6d4', emoji: '🤝', tag: '場の空気を読んで素早く動く調整役' },
    climber:      { name: 'メンター型',              sub: 'Steady Mentor',           color1: '#059669', color2: '#10b981', emoji: '🌱', tag: '着実な関係構築で信頼を積み上げる' },
    phoenix:      { name: 'エンパシーリーダー型',    sub: 'Empathy Leader',          color1: '#0891b2', color2: '#10b981', emoji: '🫂', tag: '修羅場が人間力を高める共感型リーダー' },
    breakthrough: { name: 'コミュニティビルダー型',  sub: 'Community Builder',       color1: '#0284c7', color2: '#6366f1', emoji: '🏘️', tag: '潮目を見極めて人と人をつなぐ' },
    marathoner:   { name: 'トラステッドアドバイザー型', sub: 'Trusted Advisor',      color1: '#047857', color2: '#059669', emoji: '🧭', tag: '長期的な信頼関係が最大の資産' },
  },
  BOLD_PIONEER: {
    sprinter:     { name: 'アジャイルリーダー型',    sub: 'Agile Leader',            color1: '#f97316', color2: '#ef4444', emoji: '⚡', tag: '小さく始めて素早く形にする実行家' },
    climber:      { name: 'ストラテジスト型',        sub: 'Bold Strategist',         color1: '#dc2626', color2: '#f97316', emoji: '♟️', tag: '着実な戦略で高い目標をクリアする' },
    phoenix:      { name: 'レジリエントリーダー型',  sub: 'Resilient Leader',        color1: '#b91c1c', color2: '#dc2626', emoji: '🔄', tag: '逆境をターニングポイントに変える' },
    breakthrough: { name: 'ゲームチェンジャー型',    sub: 'Game Changer',            color1: '#be185d', color2: '#e11d48', emoji: '🚀', tag: '準備を重ねて業界の常識を塗り替える' },
    marathoner:   { name: 'ビジョナリー型',          sub: 'Long-term Visionary',     color1: '#c2410c', color2: '#ea580c', emoji: '🔭', tag: '長期ビジョンを持って着実に前進する' },
  },
  FLEXIBLE_ARTISAN: {
    sprinter:     { name: 'クイックプロトタイパー型', sub: 'Quick Prototyper',       color1: '#d97706', color2: '#f59e0b', emoji: '⚙️', tag: '試して学ぶサイクルが誰より速い' },
    climber:      { name: 'クラフトマン型',          sub: 'Craftsman',               color1: '#b45309', color2: '#d97706', emoji: '🛠️', tag: '積み上げた実践知が最大の強み' },
    phoenix:      { name: 'アダプティブ型',          sub: 'Adaptive Learner',        color1: '#92400e', color2: '#b45309', emoji: '🌊', tag: '環境変化を成長の糧にできる適応力' },
    breakthrough: { name: 'イノベーター型',          sub: 'Hands-on Innovator',      color1: '#78350f', color2: '#a16207', emoji: '🔮', tag: '現場経験から革新的な発想が生まれる' },
    marathoner:   { name: 'コンシステント型',        sub: 'Consistent Performer',    color1: '#a16207', color2: '#ca8a04', emoji: '📈', tag: '継続的な実践が圧倒的な専門性を生む' },
  },
  default: {
    default:      { name: 'エクスプローラー型',      sub: 'Explorer',                color1: '#4f46e5', color2: '#7c3aed', emoji: '🌍', tag: '自分のスタイルを探求中の成長株' },
  }
};

const getTypeData = (archetype, growthType) => {
  const a = TYPE_MATRIX[archetype] || TYPE_MATRIX.default;
  return a[growthType] || a.default || TYPE_MATRIX.default.default;
};

const GROWTH_LABELS = {
  sprinter:     { label: 'スプリンター', emoji: '⚡' },
  climber:      { label: 'クライマー',   emoji: '🧗' },
  phoenix:      { label: 'フェニックス', emoji: '🔥' },
  breakthrough: { label: 'ブレイクスルー', emoji: '💥' },
  marathoner:   { label: 'マラソナー',   emoji: '🏃' },
};

const ARCHETYPE_LABELS = {
  LOGICAL_ARCHITECT:    '論理的思考',
  EMPATHETIC_CONNECTOR: '共感的連結',
  BOLD_PIONEER:         '大胆な実行',
  FLEXIBLE_ARTISAN:     '柔軟な実践',
};

export default function ShareCard({ totalHours, streak, archetype, growthType, userName, neurodiversityProfile }) {
  const [isOpen, setIsOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const canvasRef = useRef(null);
  const APP_URL = 'https://www.nobishiro-competency.com';

  const typeData = getTypeData(archetype, growthType);

  const drawCanvas = () => {
    if (!canvasRef.current) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    canvas.width  = 600;
    canvas.height = 380;

    // ── 背景 ──
    const bg = ctx.createLinearGradient(0, 0, 600, 380);
    bg.addColorStop(0, '#0f0c29');
    bg.addColorStop(0.5, '#1e1b4b');
    bg.addColorStop(1, '#111827');
    ctx.fillStyle = bg;
    ctx.fillRect(0, 0, 600, 380);

    // タイプカラーのオーバーレイ
    const overlay = ctx.createRadialGradient(500, 80, 0, 500, 80, 280);
    overlay.addColorStop(0, typeData.color1 + '30');
    overlay.addColorStop(1, 'transparent');
    ctx.fillStyle = overlay;
    ctx.fillRect(0, 0, 600, 380);

    // 左カラーバー
    const bar = ctx.createLinearGradient(0, 0, 0, 380);
    bar.addColorStop(0, typeData.color1);
    bar.addColorStop(1, typeData.color2);
    ctx.fillStyle = bar;
    ctx.fillRect(0, 0, 5, 380);

    // ── ブランド ──
    ctx.fillStyle = 'rgba(255,255,255,0.45)';
    ctx.font = 'bold 11px Arial';
    ctx.fillText('NOBISHIRO', 18, 22);
    ctx.fillStyle = 'rgba(255,255,255,0.25)';
    ctx.font = '9px Arial';
    ctx.fillText('成長タイプ診断', 18, 34);

    // ── 区切り ──
    ctx.strokeStyle = typeData.color1 + '50';
    ctx.lineWidth = 0.5;
    ctx.beginPath(); ctx.moveTo(18, 42); ctx.lineTo(582, 42); ctx.stroke();

    // ── エモジ + タイプ名 ──
    ctx.font = '44px Arial';
    ctx.fillText(typeData.emoji, 18, 92);

    ctx.fillStyle = '#ffffff';
    ctx.font = 'bold 32px Arial';
    ctx.fillText(typeData.name, 76, 80);

    const subGrad = ctx.createLinearGradient(76, 0, 350, 0);
    subGrad.addColorStop(0, typeData.color1);
    subGrad.addColorStop(1, typeData.color2);
    ctx.fillStyle = subGrad;
    ctx.font = 'bold 13px Arial';
    ctx.fillText(typeData.sub, 76, 98);

    ctx.fillStyle = 'rgba(255,255,255,0.55)';
    ctx.font = '11px Arial';
    ctx.fillText('"' + typeData.tag + '"', 18, 118);

    // ── 区切り ──
    ctx.strokeStyle = 'rgba(255,255,255,0.12)';
    ctx.lineWidth = 0.5;
    ctx.beginPath(); ctx.moveTo(18, 128); ctx.lineTo(582, 128); ctx.stroke();

    // ── 認知特性バー（3本） ──
    const neuro = neurodiversityProfile;
    const bars = neuro ? [
      {
        leftLabel: '逐次処理',
        rightLabel: '全体処理',
        value: neuro.processingStyle?.value ?? 50,
        desc: neuro.processingStyle?.value > 60 ? '全体的思考' : neuro.processingStyle?.value < 40 ? '逐次的思考' : 'バランス型',
      },
      {
        leftLabel: '収束的思考',
        rightLabel: '発散的思考',
        value: neuro.thinkingStyle?.value ?? 50,
        desc: neuro.thinkingStyle?.value > 60 ? '発散的思考' : neuro.thinkingStyle?.value < 40 ? '収束的思考' : 'バランス型',
      },
      {
        leftLabel: '持続的注意',
        rightLabel: '変動的注意',
        value: neuro.attentionPattern?.value ?? 50,
        desc: neuro.attentionPattern?.value > 60 ? 'マルチタスク型' : neuro.attentionPattern?.value < 40 ? '集中特化型' : 'バランス型',
      },
    ] : null;

    if (bars) {
      ctx.fillStyle = 'rgba(255,255,255,0.5)';
      ctx.font = 'bold 10px Arial';
      ctx.fillText('認知特性プロファイル', 18, 146);

      bars.forEach((b, i) => {
        const y = 158 + i * 36;
        const barW = 340;
        const barX = 18;

        // ラベル左右
        ctx.fillStyle = 'rgba(255,255,255,0.35)';
        ctx.font = '9px Arial';
        ctx.fillText(b.leftLabel, barX, y - 2);
        ctx.textAlign = 'right';
        ctx.fillText(b.rightLabel, barX + barW, y - 2);
        ctx.textAlign = 'left';

        // バー背景
        ctx.fillStyle = 'rgba(255,255,255,0.1)';
        ctx.beginPath();
        if (ctx.roundRect) ctx.roundRect(barX, y + 2, barW, 10, 5);
        else ctx.rect(barX, y + 2, barW, 10);
        ctx.fill();

        // バー本体
        const fillW = (b.value / 100) * barW;
        const fillGrad = ctx.createLinearGradient(barX, 0, barX + barW, 0);
        fillGrad.addColorStop(0, typeData.color1 + 'cc');
        fillGrad.addColorStop(1, typeData.color2);
        ctx.fillStyle = fillGrad;
        ctx.beginPath();
        if (ctx.roundRect) ctx.roundRect(barX, y + 2, fillW, 10, 5);
        else ctx.rect(barX, y + 2, fillW, 10);
        ctx.fill();

        // 値ラベル
        ctx.fillStyle = typeData.color1;
        ctx.font = 'bold 9px Arial';
        ctx.fillText(b.desc, barX + barW + 8, y + 11);
      });
    }

    // ── 区切り ──
    const divY = bars ? 270 : 140;
    ctx.strokeStyle = 'rgba(255,255,255,0.12)';
    ctx.lineWidth = 0.5;
    ctx.beginPath(); ctx.moveTo(18, divY); ctx.lineTo(582, divY); ctx.stroke();

    // ── 統計3項目 ──
    const statY = divY + 14;
    const statItems = [
      { label: '累計学習時間', value: Math.round(totalHours * 10) / 10 + 'h', icon: '📚' },
      { label: '連続学習',     value: streak + '日',                           icon: '🔥' },
      { label: '1000h達成率',  value: Math.min(Math.round(totalHours / 10), 100) + '%', icon: '🎯' },
    ];
    statItems.forEach((s, i) => {
      const x = 18 + i * 185;
      ctx.fillStyle = 'rgba(255,255,255,0.35)';
      ctx.font = '9px Arial';
      ctx.fillText(s.icon + ' ' + s.label, x, statY);
      ctx.fillStyle = '#ffffff';
      ctx.font = 'bold 20px Arial';
      ctx.fillText(s.value, x, statY + 22);
    });

    // ── 成長・アーキタイプバッジ ──
    const badgeY = statY + 40;
    const badges = [
      growthType ? (GROWTH_LABELS[growthType]?.emoji + ' ' + GROWTH_LABELS[growthType]?.label + '型') : null,
      archetype ? ARCHETYPE_LABELS[archetype] : null,
    ].filter(Boolean);

    badges.forEach((badge, i) => {
      const bx = 18 + i * 160;
      ctx.fillStyle = typeData.color1 + '33';
      ctx.strokeStyle = typeData.color1 + '66';
      ctx.lineWidth = 1;
      ctx.beginPath();
      if (ctx.roundRect) ctx.roundRect(bx, badgeY, 148, 22, 11);
      else ctx.rect(bx, badgeY, 148, 22);
      ctx.fill(); ctx.stroke();
      ctx.fillStyle = typeData.color1;
      ctx.font = 'bold 10px Arial';
      ctx.fillText(badge, bx + 10, badgeY + 15);
    });

    // ── フッター ──
    ctx.fillStyle = 'rgba(255,255,255,0.2)';
    ctx.font = '9px Arial';
    ctx.fillText('あなたのタイプは？ → ' + APP_URL, 18, 370);
    if (userName) {
      ctx.textAlign = 'right';
      ctx.fillStyle = typeData.color1 + 'bb';
      ctx.font = 'bold 10px Arial';
      ctx.fillText('@' + userName, 582, 370);
      ctx.textAlign = 'left';
    }
  };

  useEffect(() => {
    if (isOpen) setTimeout(drawCanvas, 50);
  }, [isOpen, typeData, totalHours, streak, neurodiversityProfile]);

  const getShareText = () =>
    `【成長タイプ診断やってみた】\n私は「${typeData.name}」タイプ。\n"${typeData.tag}"\n\n自分の強みって、見える化すると面白い。\nあなたはどのタイプ？一緒にやってみませんか？\n→ ${APP_URL}\n\n#社会人勉強 #スキルアップ #自己成長 #強み診断 #NOBISHIRO`;

  const handleShare = (id) => {
    const text = getShareText();
    if (id === 'x')        window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}`, '_blank');
    else if (id === 'line') window.open(`https://social-plugins.line.me/lineit/share?url=${encodeURIComponent(APP_URL)}&text=${encodeURIComponent(text)}`, '_blank');
    else if (id === 'facebook') window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(APP_URL)}&quote=${encodeURIComponent(text)}`, '_blank');
    else if (id === 'copy') { navigator.clipboard?.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 2500); }
  };

  const handleDownload = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const link = document.createElement('a');
    link.download = `nobishiro_${typeData.sub.replace(/ /g,'_')}.png`;
    link.href = canvas.toDataURL('image/png');
    link.click();
  };

  const SNS = [
    { id: 'x',        label: 'X (Twitter)', color: '#000', icon: '𝕏' },
    { id: 'line',     label: 'LINE',        color: '#06C755', icon: 'L' },
    { id: 'facebook', label: 'Facebook',    color: '#1877F2', icon: 'f' },
    { id: 'copy',     label: copied ? 'コピー済！' : 'リンクコピー', color: copied ? '#10b981' : '#6b7280', icon: '🔗' },
  ];

  if (!isOpen) return (
    <button onClick={() => setIsOpen(true)} style={{
      display: 'flex', alignItems: 'center', gap: 8,
      background: `linear-gradient(135deg, ${typeData.color1}, ${typeData.color2})`,
      color: '#fff', border: 'none', borderRadius: 12,
      padding: '10px 20px', fontSize: 13, fontWeight: 700, cursor: 'pointer',
      boxShadow: `0 4px 16px ${typeData.color1}50`,
    }}>
      <span>📤</span> 診断結果をシェア
    </button>
  );

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(8px)' }} onClick={() => setIsOpen(false)} />
      <div style={{ position: 'relative', background: '#111827', borderRadius: 24, padding: 24, width: '100%', maxWidth: 580, maxHeight: '92vh', overflowY: 'auto', boxShadow: '0 30px 80px rgba(0,0,0,0.5)', border: '1px solid rgba(255,255,255,0.1)' }}>

        {/* ヘッダー */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div>
            <h3 style={{ fontSize: 17, fontWeight: 800, color: '#fff', margin: '0 0 3px' }}>🎯 診断結果をシェア</h3>
            <p style={{ fontSize: 11, color: '#6b7280', margin: 0 }}>あなたの成長タイプを友達に教えよう</p>
          </div>
          <button onClick={() => setIsOpen(false)} style={{ background: 'rgba(255,255,255,0.1)', border: 'none', borderRadius: 8, width: 30, height: 30, cursor: 'pointer', color: '#9ca3af', fontSize: 15 }}>✕</button>
        </div>

        {/* タイプバッジ */}
        <div style={{ background: `linear-gradient(135deg, ${typeData.color1}22, ${typeData.color2}33)`, border: `1px solid ${typeData.color1}40`, borderRadius: 14, padding: '12px 16px', marginBottom: 14, display: 'flex', alignItems: 'center', gap: 12 }}>
          <span style={{ fontSize: 32 }}>{typeData.emoji}</span>
          <div>
            <div style={{ fontSize: 18, fontWeight: 800, color: '#fff' }}>{typeData.name}</div>
            <div style={{ fontSize: 12, color: typeData.color1, fontWeight: 700 }}>{typeData.sub} · "{typeData.tag}"</div>
          </div>
        </div>

        {/* Canvasプレビュー */}
        <div style={{ borderRadius: 12, overflow: 'hidden', marginBottom: 14, boxShadow: `0 6px 24px ${typeData.color1}40` }}>
          <canvas ref={canvasRef} style={{ width: '100%', display: 'block' }} />
        </div>

        {/* ダウンロード */}
        <button onClick={handleDownload} style={{ width: '100%', marginBottom: 10, padding: '9px 0', background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 12, fontSize: 13, fontWeight: 700, color: '#e0e7ff', cursor: 'pointer' }}>
          🖼️ 画像ダウンロード（Instagram用）
        </button>

        {/* SNSボタン */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
          {SNS.map(s => (
            <button key={s.id} onClick={() => handleShare(s.id)} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: '11px 0', borderRadius: 12, border: 'none', cursor: 'pointer', background: s.color, color: '#fff', fontSize: 13, fontWeight: 700 }}>
              <span style={{ fontWeight: 900 }}>{s.icon}</span>{s.label}
            </button>
          ))}
        </div>
        <p style={{ fontSize: 10, color: '#374151', textAlign: 'center', marginTop: 10 }}>※ Instagramは画像ダウンロード後、アプリから投稿してください</p>
      </div>
    </div>
  );
}
